import React, { useMemo } from 'react';
import BarcodeSvgRenderer from './BarcodeSvgRenderer';
import './ThermalSpecimenLabel.css';

/**
 * Enterprise Thermal Specimen Tube Label Renderer
 * Renders physical millimeter dimensions (50x25mm, 40x20mm, 50x30mm, 75x25mm, etc.)
 * Strictly sanitizes all data values against XSS injection attacks.
 */

// Millimeter to Screen Pixels (approx 3.78 px per mm at 96 DPI screen standard)
const MM_TO_PX = 3.78;

export const ThermalSpecimenLabel = ({
  template,
  sampleData = {},
  zoom = 1.0,
  isInteractive = false,
  selectedElementId = null,
  onSelectElement = null,
  className = '',
  style = {}
}) => {
  const widthMm = Number(template?.labelWidthMm || 50.0);
  const heightMm = Number(template?.labelHeightMm || 25.0);
  const widthPx = widthMm * MM_TO_PX * zoom;
  const heightPx = heightMm * MM_TO_PX * zoom;

  // Safe data resolver with automatic anti-XSS conversion
  const resolveToken = (field, token) => {
    const rawVal = sampleData[field] || sampleData[token] || sampleData[field?.toLowerCase()];
    if (rawVal !== undefined && rawVal !== null) {
      return String(rawVal);
    }
    // Fallback lookups
    switch (field) {
      case 'PATIENT_NAME': return sampleData.patientName || 'PATIENT NAME';
      case 'UHID': return sampleData.uhid || sampleData.patientId || 'UHID-0001';
      case 'LAB_NUMBER': return sampleData.ledgerTransactionNo || sampleData.labNo || 'L-10001';
      case 'SAMPLE_TYPE': return sampleData.sampleTypeName || 'Serum';
      case 'CONTAINER_TYPE': return sampleData.containerName || 'EDTA';
      case 'TEST_NAME': return sampleData.itemName || sampleData.testName || 'Investigation';
      case 'CENTRE_NAME': return sampleData.centreName || 'AURO DIAGNOSTICS';
      case 'COLLECTION_DATETIME': return sampleData.collectionDate ? `${sampleData.collectionDate} ${sampleData.collectionTime || ''}` : new Date().toLocaleDateString();
      case 'DOCTOR_NAME': return sampleData.doctorName || 'Ref: Self';
      case 'BARCODE': return sampleData.barcodeNo || sampleData.barcode || '10000001';
      default: return field || '';
    }
  };

  // Parse design elements
  const elements = useMemo(() => {
    if (!template?.designDataJson) return null;
    try {
      const parsed = typeof template.designDataJson === 'string'
        ? JSON.parse(template.designDataJson)
        : template.designDataJson;
      return parsed.elements || [];
    } catch {
      return null;
    }
  }, [template?.designDataJson]);

  // Default layout if template has no elements JSON
  const renderDefaultLayout = () => {
    const pName = resolveToken('PATIENT_NAME');
    const uhid = resolveToken('UHID');
    const labNo = resolveToken('LAB_NUMBER');
    const barcode = resolveToken('BARCODE');
    const testName = resolveToken('TEST_NAME');
    const sampleType = resolveToken('SAMPLE_TYPE');
    const centre = resolveToken('CENTRE_NAME');

    return (
      <div className="tsl-default-content">
        <div className="tsl-header-row">
          <span className="tsl-centre-tag">{centre}</span>
          <span className="tsl-time-tag">{new Date().toLocaleDateString()}</span>
        </div>
        <div className="tsl-patient-row">
          <strong className="tsl-pname" title={pName}>{pName}</strong>
          <span className="tsl-meta-pill">{uhid}</span>
        </div>
        <div className="tsl-sub-row">
          <span className="tsl-labno">Lab: <strong>{labNo}</strong></span>
          <span className="tsl-sampletype">{sampleType}</span>
        </div>
        <div className="tsl-barcode-container">
          <BarcodeSvgRenderer
            value={barcode}
            symbology={template?.symbology || 'CODE128'}
            showText={template?.includeHumanReadable !== false}
            height={heightPx * 0.32}
            quietZone={4}
          />
        </div>
        <div className="tsl-footer-row">
          <span className="tsl-test-tag" title={testName}>{testName}</span>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`thermal-specimen-label-card ${isInteractive ? 'interactive-mode' : ''} ${className}`}
      style={{
        width: `${widthPx}px`,
        height: `${heightPx}px`,
        minWidth: `${widthPx}px`,
        minHeight: `${heightPx}px`,
        ...style
      }}
      data-testid="thermal-specimen-label"
    >
      {/* Millimeter printable boundary watermark */}
      <div className="tsl-boundary-indicator">
        {widthMm} × {heightMm} mm
      </div>

      {elements && elements.length > 0 ? (
        <div className="tsl-elements-layer">
          {elements.map((el, idx) => {
            const isSelected = selectedElementId === el.id;
            const xPx = (el.x || 0) * MM_TO_PX * zoom;
            const yPx = (el.y || 0) * MM_TO_PX * zoom;
            const elWidthPx = (el.width || 20) * MM_TO_PX * zoom;
            const elHeightPx = (el.height || 4) * MM_TO_PX * zoom;

            const isBarcode = el.type === 'BARCODE' || el.field === 'BARCODE';
            const value = isBarcode ? resolveToken('BARCODE') : resolveToken(el.field, el.token);
            const displayValue = `${el.prefix || ''}${value}${el.suffix || ''}`;

            return (
              <div
                key={el.id || idx}
                className={`tsl-element-box ${isBarcode ? 'element-barcode' : 'element-text'} ${isSelected ? 'selected' : ''}`}
                style={{
                  position: 'absolute',
                  left: `${xPx}px`,
                  top: `${yPx}px`,
                  width: `${elWidthPx}px`,
                  height: `${elHeightPx}px`,
                  textAlign: el.align || 'left',
                  fontSize: `${(el.fontSize || 8) * zoom}px`,
                  fontFamily: el.font || 'sans-serif',
                  fontWeight: el.bold ? 'bold' : 'normal',
                  fontStyle: el.italic ? 'italic' : 'normal',
                  cursor: isInteractive ? 'pointer' : 'default'
                }}
                onClick={(e) => {
                  if (isInteractive && onSelectElement) {
                    e.stopPropagation();
                    onSelectElement(el);
                  }
                }}
              >
                {isBarcode ? (
                  <BarcodeSvgRenderer
                    value={value}
                    symbology={template?.symbology || el.symbology || 'CODE128'}
                    showText={el.showText !== false}
                    height={elHeightPx}
                    quietZone={2}
                    fontSize={Math.max(7, (el.fontSize || 8) * zoom)}
                  />
                ) : (
                  <span className="tsl-text-content" title={displayValue}>
                    {displayValue}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        renderDefaultLayout()
      )}
    </div>
  );
};

export default ThermalSpecimenLabel;
